import React, { CSSProperties } from 'react';
import { flexRender, type Table, Column } from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronsDown, ChevronsUp } from 'lucide-react';
import { DataTableColumn, FilterStyle } from "../types";
import ColumnFilter from './ColumnFilter';
import ColumnResizeHandle from './ColumnResizeHandle';
import HeaderWithTooltip from './HeaderWithTooltip';
import { getCommonPinningStyles } from "./commonStyles";

interface DataTableHeaderProps<TData> {
  table: Table<TData>;
  enableSorting?: boolean;
  enableMultiSort?: boolean;
  enableFiltering?: boolean;
  enableGrouping?: boolean;
  enableColumnResizing?: boolean;
  filterStyle?: FilterStyle;
  stickyHeaders?: boolean;
  useSteppedGroupingExpansion?: boolean;
}

export default function DataTableHeader<TData>({
  table,
  enableSorting = false,
  enableMultiSort = false,
  enableFiltering = false,
  enableGrouping = false,
  enableColumnResizing = false,
  filterStyle = 'select',
  stickyHeaders = false,
  useSteppedGroupingExpansion = false,
}: DataTableHeaderProps<TData>) {
  const sorting = table.getState().sorting;
  const grouping = table.getState().grouping;
  const expanded = table.getState().expanded;

  const getSortIndex = (columnId: string) => {
    return sorting.findIndex(s => s.id === columnId);
  };

  const handleSteppedGroupExpansion = () => {
    const allExpanded = table.getIsAllRowsExpanded();
    if (allExpanded) {
      table.toggleAllRowsExpanded(!allExpanded);
      return;
    }
    const rowModel = table.getRowModel();
    const currentExpanded = table.getState().expanded;
    const groupingDepth = table.getState().grouping.length;

    if (groupingDepth === 0) return; // No grouping, nothing to do

    // Determine current expansion depth
    let currentDepth = -1; // -1 means all collapsed

    if (currentExpanded === true) {
      currentDepth = groupingDepth; // All expanded
    } else if (typeof currentExpanded === 'object') {
      if (Object.keys(currentExpanded).length === 0) {
        currentDepth = 0;
      } else {
        // Find the maximum depth that's expanded
        const depths = rowModel.rows
          .filter(row => currentExpanded[row.id])
          .map(row => row.depth + 1);
        currentDepth = depths.length > 0 ? Math.max(...depths) : -1;
      }
    }


    // Cycle to next state
    const nextDepth = currentDepth + 1;

    if (nextDepth > groupingDepth) {
      // Collapse all
      table.setExpanded({});
    } else if (nextDepth === groupingDepth) {
      // Expand everything
      table.setExpanded(true);
    } else {
      // Expand up to nextDepth
      const newExpanded: Record<string, boolean> = {};
      rowModel.rows.forEach(row => {
        if (row.depth < nextDepth) {
          newExpanded[row.id] = true;
        }
      });
      table.setExpanded(newExpanded);
    }
  }

  const handleToggleAllGroups = () => {
    const allExpanded = table.getIsAllRowsExpanded();
    if (!useSteppedGroupingExpansion) {
      table.toggleAllRowsExpanded(!allExpanded);
    } else {
      handleSteppedGroupExpansion();
    }
  };

  const handleColumnSort = (columnId: string) => {
    // If multi-sort is enabled and there are multiple sorts active,
    // reset to single column sort when clicking a column header
    if (enableMultiSort && sorting.length > 0) {
      const currentSort = sorting.find(s => s.id === columnId);
      if (!currentSort) {
        // Column not currently sorted, set it as the only sort
        table.setSorting([{ id: columnId, desc: false }]);
      } else if (currentSort.desc === false) {
        // Currently ascending, switch to descending
        table.setSorting([{ id: columnId, desc: true }]);
      } else {
        // Currently descending, clear sort
        table.setSorting([]);
      }
    } else {
      // Use default toggle behavior for single sort
      const column = table.getColumn(columnId);
      if (column) {
        column.toggleSorting();
      }
    }
  };

  return (
    <thead className={`bg-gray-50 ${stickyHeaders ? 'sticky top-0 z-10' : ''}`}>
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const canSort = enableSorting && header.column.getCanSort();
            const canFilter = enableFiltering && header.column.getCanFilter();
            const isSorted = header.column.getIsSorted();
            const sortIndex = getSortIndex(header.column.id);
            const columnDef = header.column.columnDef as DataTableColumn<TData>;

            return (
              <th
                key={header.id}
                className="pr-1 pl-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative"
                style={{
                  width: enableColumnResizing ? header.getSize() : columnDef.size,
                  minWidth: columnDef.minSize,
                  maxWidth: columnDef.maxSize,
                  ...getCommonPinningStyles(header.column),
                }}
              >
                {header.isPlaceholder ? null : (
                  <div className={`flex items-center overflow-hidden ${enableColumnResizing ? 'pr-4' : 'pr-2'}`}>
                    {enableGrouping && grouping.includes(header.column.id) && (
                      <button
                        onClick={handleToggleAllGroups}
                        className="hover:bg-gray-200 p-1 rounded flex-shrink-0"
                        title={table.getIsAllRowsExpanded() ? "Collapse all groups" : "Expand all groups"}
                        data-testid={table.getIsAllRowsExpanded() ? "collapse-all-button" : "expand-all-button"}
                      >
                        {table.getIsAllRowsExpanded() ? (
                          <ChevronsUp className="h-4 w-4 text-gray-600" />
                        ) : (
                          <ChevronsDown className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                    )}
                    <HeaderWithTooltip
                      headerText={typeof header.column.columnDef.header === 'string'
                        ? header.column.columnDef.header
                        : header.column.id}
                      className="flex-1 min-w-0"
                    >
                      <div className="truncate">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    </HeaderWithTooltip>
                    {canSort && (
                      <HeaderWithTooltip
                        headerText={typeof header.column.columnDef.header === 'string'
                          ? header.column.columnDef.header
                          : header.column.id}
                        className="flex items-center gap-1 flex-shrink-0"
                      >
                        <button
                          onClick={() => handleColumnSort(header.column.id)}
                          className="hover:bg-gray-200 p-1 rounded flex items-center gap-1"
                          data-testid={`column-sort-button-${header.column.id}`}
                          title={
                            isSorted === 'asc'
                              ? 'Sorted ascending'
                              : isSorted === 'desc'
                                ? 'Sorted descending'
                                : 'Click to sort'
                          }
                        >
                          {isSorted === false && (
                            <ArrowUpDown className="h-4 w-4 text-gray-300" />
                          )}
                          {isSorted === 'asc' && (
                            <ArrowUp className={`h-4 w-4 ${sortIndex === 0 ? 'text-blue-600' : 'text-gray-600'}`} />
                          )}
                          {isSorted === 'desc' && (
                            <ArrowDown className={`h-4 w-4 ${sortIndex === 0 ? 'text-blue-600' : 'text-gray-600'}`} />
                          )}
                        </button>
                        {isSorted && sorting.length > 1 && (
                          <span className={`
                            text-xs font-semibold px-0.5 py-0.5 rounded-full
                            ${sortIndex === 0
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-gray-100 text-gray-600'
                            }
                          `}>
                            {sortIndex + 1}
                          </span>
                        )}
                      </HeaderWithTooltip>
                    )}
                    {canFilter && columnDef.enableFiltering !== false && (
                      <div className="flex-shrink-0">
                        <ColumnFilter column={header.column} table={table} filterStyle={filterStyle} />
                      </div>
                    )}
                  </div>
                )}
                {enableColumnResizing && (
                  <ColumnResizeHandle
                    header={header}
                    isResizing={header.column.getIsResizing()}
                  />
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}
